// Collision guard for buffer-local rename (see `bufferRenamePlan.ts`).
//
// Renaming an alias or CTE to a name that is ALREADY visible in the same
// statement silently changes which relation a `x.col` reference resolves to —
// the SQL still parses and still runs, on the wrong data. The guard refuses
// instead, and the provider surfaces the reason through Monaco's `Rejection`
// channel so the user sees WHY the rename did not happen.
//
// KNOWN LIMIT — a DELIMITED new name. The typed name is folded as if it were
// an undelimited identifier, so typing `"O"` into the rename box folds to
// `"o"` (delimiters included) and matches no binding: a genuine clash with an
// existing `"O"` alias is missed and the rename goes through. Not fixed here
// because the "the typed name arrives undelimited" assumption is shared by the
// unchanged-name check and by `renderRenamedToken` (which passes a delimited
// name through verbatim); unpicking it needs one shared "parse the typed new
// name" helper across all three, not a local tweak to this fold. Typing an
// UNdelimited name — the overwhelmingly common case, and the only shape
// Monaco's rename box pre-fills — is fully guarded.

import { computeAliasBindingsForRange } from '../../sql-resolver/scope/aliasScope';
import { computeCteBindingsForRange } from '../../sql-resolver/scope/cteScope';
import { findReferencedRelationsForRange } from '../../sql-resolver/scope/findReferencedRelations';

import { foldFor, type BufferContext, type BufferRenameTarget } from './bufferRenameTarget';

/** Returns a human phrase naming what the new name would clash with, or null.
 *  Checks every binding visible in the owning statement — sibling aliases,
 *  sibling CTEs, and the relations the statement actually references (so
 *  renaming `u` to `users` in `FROM users u` is refused). */
export function findBufferRenameCollision(
  ctx: BufferContext,
  target: BufferRenameTarget,
  newName: string,
): string | null {
  const start = target.statement.range.startOffset;
  const end = target.statement.range.endOffset;
  const folded = foldFor(ctx, newName, false);

  const aliases = computeAliasBindingsForRange(ctx.buffer, start, end);
  for (const a of aliases) {
    if (a.range.startOffset === target.alias?.range.startOffset) continue;
    if (a.aliasFolded === folded) return 'an alias';
  }
  const ctes = computeCteBindingsForRange(ctx.buffer, start, end);
  for (const c of ctes) {
    if (
      c.nameRange !== undefined &&
      c.nameRange.startOffset === target.cte?.nameRange?.startOffset
    ) continue;
    if (c.name.foldedName === folded) return 'a CTE';
  }
  for (const rel of findReferencedRelationsForRange(ctx.buffer, start, end)) {
    // A CTE reference surfaces here too; it was already reported above.
    if (ctes.some((c) => c.name.foldedName === rel.foldedName)) continue;
    if (rel.foldedName === folded) return 'a table';
  }
  return null;
}
